import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { formatInstant } from '@/shared/model/instant';

import type { ProfileReading } from '../model/catalogue';

/* A row count and a profile date, or an honest blank. Never a zero standing in for either.
 *
 * `ScoreCell` is the precedent and the reasoning is the same one level over: a figure this platform
 * did not measure must not render as a figure it did. There are four readings and each gets its own
 * words, because collapsing any two of them puts a false statement in a table cell:
 *
 *   - `unread`        -> the facts request failed. About us, not about the data. Says so.
 *   - `neverProfiled` -> nobody has profiled this table. **Not `0 rows`**, which would report every
 *                        unmeasured table in the estate as empty — on the screen a steward uses to
 *                        decide what to measure next.
 *   - `profiledEmpty` -> a profile ran and the columns reported no row count. Measured, and the
 *                        finding is that there is nothing there.
 *   - `measured`      -> a real pair.
 *
 * **One component renders both columns**, selected by `show`, and that is deliberate rather than a
 * convenience: the row count and the date are two halves of one reading, and two components would be
 * two places the four-way distinction has to be kept — which is how one of them comes to render a
 * count for a reading the other calls never-profiled.
 */

export interface ProfileCellProps {
  readonly reading: ProfileReading;
  /** Which half of the reading this cell shows. */
  readonly show: 'rows' | 'when';
  /** `true` while the facts request is in flight.
   *
   * Separate from `reading` on purpose. In flight, `facts.data` is undefined and every row's reading
   * is `neverProfiled` — which is a claim about the customer's data made while we are still asking.
   * A pending cell says nothing at all instead.
   */
  readonly pending: boolean;
}

export function ProfileCell({ reading, show, pending }: ProfileCellProps): ReactElement {
  const { t } = useTranslation();

  if (pending) {
    /* Deliberately not a skeleton. One cell of a table that already has rows is a flicker rather
       than a loading state, and the row's other columns are already readable. */
    return (
      <span className="lq-dim" aria-hidden="true">
        ·
      </span>
    );
  }

  if (reading.kind === 'unread') {
    return (
      <span className="lq-muted" title={t('catalogue.profile.unread.meaning')}>
        {t('catalogue.profile.unread.label')}
      </span>
    );
  }

  if (reading.kind === 'neverProfiled') {
    return show === 'rows' ? (
      <span className="lq-muted" title={t('catalogue.profile.never.meaning')}>
        &mdash;
      </span>
    ) : (
      <span className="lq-muted" title={t('catalogue.profile.never.meaning')}>
        {t('catalogue.profile.never.label')}
      </span>
    );
  }

  if (reading.kind === 'profiledEmpty') {
    return show === 'rows' ? (
      /* The word rather than `0`. A profile that reported no row count is not the same as a table
         measured at zero rows, and the sketch store cannot tell us which — so the cell says what it
         knows: the profile ran and this figure is not in it. */
      <span className="lq-muted" title={t('catalogue.profile.empty.meaning')}>
        {t('catalogue.profile.empty.label')}
      </span>
    ) : (
      <span title={t('catalogue.profile.at', { at: new Date(reading.at) })}>
        {t('catalogue.profile.ago', {
          at: formatInstant(reading.at) ?? reading.at,
        })}
      </span>
    );
  }

  return show === 'rows' ? (
    <span title={t('catalogue.profile.rowsMeaning')}>
      {reading.rows.toLocaleString()}
    </span>
  ) : (
    <span title={t('catalogue.profile.at', { at: new Date(reading.at) })}>
      {t('catalogue.profile.ago', {
        at: formatInstant(reading.at) ?? reading.at,
      })}
    </span>
  );
}
